import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import shortid from 'shortid';
import Loading from 'components/Loading/index';
import api from 'api';
import Util from 'utility';

import {
    setData,
    resetRecipe,
    deleteEmptyFoodGroups,
    addFoodGroup,
    addFood,
    setFoodGroupDescription,
    addFoodModification,
    setTags,
    setRecipeTitle,
    setActiveMealHash,
    setPlannerEditMode,
    openModal,
    closeModal,
} from 'actions/index';

import MenuPanel from './MenuPanel/index';
import MainPanel from './MainPanel/index';
import EvaluationPanel from 'components/EvaluationPanel';
import EvaluationPanelContents from 'components/EvaluationPanel/EvaluationPanelContents';

function cleanLocalStoredRecipe(data) {
    const result = {
        foodGroups: {},
        foods: {},
        titles: {},
        descriptions: {},
    };

    Object.keys(data.foodGroups).forEach(k => {
        if (k === '1|0') result.foodGroups[k] = data.foodGroups[k];
    });

    if (Object.keys(result.foodGroups).length === 0) {
        const g = shortid();
        result.foodGroups['1|0'] = {};
        result.foodGroups['1|0'][g] = { id: g, title: '', description: '', type: 'plate', opened: true };
    }

    Object.keys(data.foods).forEach(k => {
        if (k === '1|0') result.foods[k] = data.foods[k];
    });

    Object.keys(data.titles).forEach(k => {
        if (k === '1|0') result.titles[k] = data.titles[k];
    });

    Object.keys(data.descriptions || {}).forEach(k => {
        if (k === '1|0') result.descriptions[k] = data.descriptions[k];
    });

    return result;
}

function RecipeEditor({ action }) {
    const data = useSelector(state => state);
    const dispatch = useDispatch();
    const history = useHistory();
    const { id: paramId, hash: paramHash } = useParams();

    const [loading, setLoading] = useState(true);
    const [recipeTitle, setRecipeTitleState] = useState('');

    useEffect(() => {
        Util.resetCookbookFilter();
        Util.resetFoodsFilter();
        Util.setPageTitle('Úprava receptu');

        const localStoredRecipe = localStorage.getItem('zof_unsaved_recipe');
        const activeMealHash = data.activeMealHash !== '' ? data.activeMealHash : '1|0';

        dispatch(setPlannerEditMode(''));

        if (!localStoredRecipe) {
            dispatch(setData({ lastOpenedCookBookRecipe: -1 }));
        }

        if (typeof paramId === 'undefined' && typeof paramHash === 'undefined') {
            if (localStoredRecipe) {
                dispatch(resetRecipe());
                const g = shortid();
                dispatch(addFoodGroup({ id: g, title: '', description: '', type: 'plate', opened: true }));
                let stored = JSON.parse(localStoredRecipe);
                stored = cleanLocalStoredRecipe(stored);
                dispatch(setData(stored));
            } else {
                dispatch(resetRecipe());
                const g = shortid();
                dispatch(addFoodGroup({ id: g, title: '', description: '', type: 'plate', opened: true }));
            }
            setLoading(false);
            Util.doLoadEvaluation();
        } else if (paramId) {
            api('recipes/add/' + paramId).then(result => {
                if (typeof result.data.error !== 'undefined') {
                    history.push('/recipe');
                    return;
                }
                dispatch(resetRecipe());
                dispatch(deleteEmptyFoodGroups());
                const res = result.data;
                Object.keys(res.groups).forEach(g => {
                    const group = res.groups[g];
                    dispatch(addFoodGroup({ id: group.group_id, title: group.title, type: group.type, opened: true }));
                    dispatch(setFoodGroupDescription({ id: group.group_id, description: group.description }));
                });
                Object.keys(res.foods).forEach(f => {
                    const food = res.foods[f];
                    const newFoodId = shortid();
                    dispatch(addFood({
                        id: newFoodId,
                        food_id: food.food_id,
                        group_id: food.group_id,
                        is_drink: food.is_drink,
                        weight: food.amount,
                        image: 'https://www.zofapp.cz/data/zof/item/' + food.food_id + '.svg',
                        title: data.globals.itemData[food.food_id]['title'],
                    }));
                    Object.keys(food.modifications).forEach(m => {
                        dispatch(addFoodModification({ foodId: newFoodId, modificationId: food.modifications[m] }));
                    });
                });
                dispatch(setRecipeTitle({ mealHash: activeMealHash, title: res.title }));
                dispatch(setTags(res.tags));
                dispatch(setData({ lastOpenedCookBookRecipe: paramId }));
                setRecipeTitleState(res.title);
                setLoading(false);
                Util.doLoadEvaluation();
            });
        } else if (paramHash) {
            const mealHash = paramHash.replace('_', '|');
            let createNewFoodGroup = false;
            if (typeof data.foodGroups[mealHash] === 'undefined') {
                createNewFoodGroup = true;
            } else if (Object.keys(data.foodGroups[mealHash]).length === 0) {
                createNewFoodGroup = true;
            }
            if (createNewFoodGroup) {
                const g = shortid();
                dispatch(addFoodGroup({ id: g, title: '', description: '', type: 'plate', opened: true }));
            }
            dispatch(setActiveMealHash(mealHash));
            if (typeof data.meals[data.activeMealHash] === 'object') {
                const mealId = data.meals[data.activeMealHash].mealId;
                if (data.globals.mealTypes[mealId].gastro_only === true) {
                    dispatch(setPlannerEditMode('GASTRO_DAILY_MENU'));
                }
            }
            setLoading(false);
            Util.doLoadEvaluation();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const updateRecipeTitle = (e) => {
        dispatch(setRecipeTitle({ mealHash: data.activeMealHash, title: e.target.value }));
        Util.saveCurrentRecipeLocal();
    };

    const openRecipeEditorHelp = () => {
        const helpContent = (
            <div className="p-[0.5em]">
                <p>Recept je vytvořen v <a style={{ color: '#4eb32d' }} href="https://www.zofapp.cz" target="_blank" rel="noreferrer">aplikaci ZOF</a>. Obsahuje:</p>
                <p><b>1. Seznam surovin</b></p>
                <p>U každé potraviny je uveden:</p>
                <ul>
                    <li>Objem/hmotnost (v gramech nebo mililitrech) - tyto hodnoty můžete upravovat.</li>
                    <li>Seznam alergenů (pokud je obsahuje) - A1 - A14.</li>
                    <li>Způsob kulinářské úpravy (můžete měnit).</li>
                </ul>
                <p><b>2. Popis potraviny</b></p>
                <p>Zobrazí se kliknutím na otazník u každé suroviny.</p>
                <p><b>3. Popis přípravy receptu</b></p>
                <p>Zobrazí se kliknutím na tlačítko "Popis přípravy".</p>
                <p><b>4. Nutriční vyhodnocení</b></p>
                <p>Nachází se v pravé části receptu, zobrazuje více než 60 nutričních hodnot. Při úpravě množství surovin v receptu se budou měnit i nutriční charakteristiky. U každé nutriční hodnoty se nachází otazník, kliknutím na něj se zobrazí bližší informace o sledované živině.</p>
            </div>
        );
        dispatch(closeModal());
        dispatch(openModal({ title: 'Nápověda', class: 'dialog-flexible', content: helpContent }));
    };

    if (loading) return <Loading />;

    const recipeNameInput = (
        typeof action === 'undefined' || action === '' || action === 'recipe-new'
    ) ? (
        <div className="max-w-[1180px] mx-auto px-[1em]" style={{ flex: '0', padding: '.5em 1em' }}>
            <input className="flex-1 mr-[0.5em] border border-[#bbb] rounded px-[5px] py-[3px] font-bold" type="text" onChange={updateRecipeTitle} placeholder="Zde napište název receptu" value={data.titles[data.activeMealHash]} />
        </div>
    ) : false;

    const panelParameters = { hash: paramHash };

    if (Util.isLectureLikeMode(data.appMode) && typeof data.appModeData.lectureInfoData === 'object') {
        return (
            <div className="w-full flex flex-col bg-[#f7f8fa]">
                {recipeNameInput}
                <MenuPanel action={action} initialRecipeName={recipeTitle} parameters={panelParameters} />
                <div className="border-t border-[#e0e4eb] flex flex-1 flex-col">
                    <div className="max-w-[1180px] mx-auto px-[1em]" style={{ flex: '1', paddingTop: '1em', paddingBottom: '1em' }}>
                        <input className="radio-plan-mobile-nav" name="plan-nav" type="radio" id="cb-plan-main" defaultChecked={true} />
                        <input className="radio-plan-mobile-nav" name="plan-nav" type="radio" id="cb-plan-evaluation" />
                        <div className="plan-mobile-nav">
                            <label htmlFor="cb-plan-main"><i className="fa fa-angle-down"></i> Rozpis</label>
                            <label htmlFor="cb-plan-evaluation"><i className="fa fa-angle-down"></i> Vyhodnocení</label>
                        </div>
                        <div className="plan">
                            <div className="plan-row">
                                <input type="checkbox" id="cb-panel-evaluation" />
                                <div className="plan-main">
                                    <MainPanel action={action} parameters={panelParameters} />
                                </div>
                                <div style={{ flexBasis: '300px', flexShrink: '0', order: 3 }}>
                                    <EvaluationPanelContents
                                        wholeDay={false}
                                        results={data.evaluationResults.results}
                                        ddd={data.evaluationResults.ddd}
                                        mainStyle={{ display: 'block' }}
                                        quickResults={false}
                                        evaluationCollapsedAtStart={false}
                                        macrosOnly={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col bg-[#f7f8fa]">
            {recipeNameInput}
            <MenuPanel action={action} initialRecipeName={recipeTitle} parameters={panelParameters} />
            <div className="border-t border-[#e0e4eb] flex flex-1 flex-col">
                <div className="max-w-[1180px] mx-auto px-[1em]" style={{ flex: '1', paddingTop: '1em', paddingBottom: '1em' }}>
                    <input className="radio-plan-mobile-nav" name="plan-nav" type="radio" id="cb-plan-main" defaultChecked={true} />
                    <input className="radio-plan-mobile-nav" name="plan-nav" type="radio" id="cb-plan-evaluation" />
                    <div className="plan-mobile-nav">
                        <label htmlFor="cb-plan-main"><i className="fa fa-angle-down"></i> Rozpis</label>
                        <label htmlFor="cb-plan-evaluation"><i className="fa fa-angle-down"></i> Vyhodnocení</label>
                    </div>
                    <div className="plan">
                        <div className="plan-row">
                            <input type="checkbox" id="cb-panel-evaluation" />
                            <div className="plan-main">
                                <MainPanel action={action} parameters={panelParameters} />
                            </div>
                            <EvaluationPanel
                                beforeContent={false}
                                wholeDay={false}
                                quickResults={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RecipeEditor;
